import { useState, useEffect } from "react";
import { useGetIdentity } from "@refinedev/core";
import {
  Typography,
  Row,
  Col,
  Card,
  Descriptions,
  Form,
  Input,
  Button,
  Space,
  Divider,
  Avatar,
  Spin,
  notification,
  Modal,
  Grid,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  LockOutlined,
  SaveOutlined,
  CloseOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../providers/dataProvider";
import { MfaSecurityCard } from "./MfaSecurityCard";
import type {
  UserProfile,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from "../../types";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { data: identity } = useGetIdentity<{ id: string; name: string }>();
  const screens = useBreakpoint();

  // Responsive values
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const labelWidth = screens.md ? "220px" : screens.sm ? "140px" : "120px";
  const descriptionColumns = screens.md ? 2 : 1;
  const pagePadding = isMobile ? 12 : isTablet ? 16 : 24;
  const gutter: [number, number] = isMobile ? [12, 12] : [24, 24];
  const avatarSize = isMobile ? 80 : 100;

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // Note: axiosInstance has baseURL set, so use relative path without leading slash
      const { data } = await axiosInstance.get("users/me");
      setProfile(data);
    } catch (error) {
      notification.error({ message: t("profile.fetchError") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile && editing) {
      form.setFieldsValue({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        telephone: profile.telephone,
        cell: profile.cell,
      });
    }
  }, [profile, editing, form]);

  const handleSave = async (values: UpdateProfileRequest) => {
    try {
      setSaving(true);
      await axiosInstance.put("users/me", values);
      notification.success({ message: t("profile.updateSuccess") });
      setEditing(false);
      fetchProfile();
    } catch (error) {
      notification.error({ message: t("profile.updateError") });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (values: ChangePasswordRequest) => {
    if (values.newPassword !== values.confirmPassword) {
      notification.error({ message: t("profile.credentialMismatch") });
      return;
    }

    try {
      setChangingPassword(true);
      await axiosInstance.post("users/me/change-credential", {
        currentCredential: values.currentPassword,
        newCredential: values.newPassword,
      });
      notification.success({ message: t("profile.credentialChangeSuccess") });
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      notification.error({ message: t("profile.credentialChangeError") });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    form.resetFields();
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: pagePadding }}>
      <Row gutter={gutter}>
        {/* Profile Header */}
        <Col span={24}>
          <Card size={isMobile ? "small" : "default"}>
            <Row gutter={[16, 16]} align="middle">
              <Col
                xs={24}
                sm={24}
                md="auto"
                style={{ textAlign: isMobile ? "center" : "left" }}
              >
                <Avatar
                  size={avatarSize}
                  icon={<UserOutlined />}
                  src={profile?.picture}
                  style={{ backgroundColor: "#1890ff" }}
                />
              </Col>
              <Col
                xs={24}
                sm={24}
                md="auto"
                flex={isMobile ? undefined : "auto"}
                style={{ textAlign: isMobile ? "center" : "left" }}
              >
                <Title level={isMobile ? 4 : 3} style={{ marginBottom: 4 }}>
                  {profile?.firstName && profile?.lastName
                    ? `${profile.firstName} ${profile.lastName}`
                    : profile?.loginName || identity?.name}
                </Title>
                <Text type="secondary">@{profile?.loginName}</Text>
                {profile?.email && (
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      <MailOutlined />
                      <Text
                        style={{
                          fontSize: isMobile ? 13 : 14,
                          wordBreak: "break-all",
                        }}
                      >
                        {profile.email}
                      </Text>
                    </Space>
                  </div>
                )}
              </Col>
              <Col
                xs={24}
                sm={24}
                md="auto"
                style={{ textAlign: isMobile ? "center" : "right" }}
              >
                <Space
                  direction={isMobile ? "vertical" : "horizontal"}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  {!editing && (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => setEditing(true)}
                      block={isMobile}
                    >
                      {t("profile.edit")}
                    </Button>
                  )}
                  <Button
                    icon={<LockOutlined />}
                    onClick={() => setPasswordModalVisible(true)}
                    block={isMobile}
                  >
                    {t("profile.changeCredential")}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Profile Information */}
        <Col span={24}>
          <Card
            title={t("profile.personalInformation")}
            size={isMobile ? "small" : "default"}
            extra={
              editing && (
                <Space size={isMobile ? "small" : "middle"}>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEdit}
                    size={isMobile ? "small" : "middle"}
                  >
                    {!isMobile && t("actions.cancel")}
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={() => form.submit()}
                    size={isMobile ? "small" : "middle"}
                  >
                    {!isMobile && t("actions.save")}
                  </Button>
                </Space>
              )
            }
          >
            {editing ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                initialValues={{
                  firstName: profile?.firstName,
                  lastName: profile?.lastName,
                  email: profile?.email,
                  telephone: profile?.telephone,
                  cell: profile?.cell,
                }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label={t("profile.firstName")} name="firstName">
                      <Input placeholder={t("profile.enterFirstName")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t("profile.lastName")} name="lastName">
                      <Input placeholder={t("profile.enterLastName")} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={t("profile.email")}
                      name="email"
                      rules={[
                        { type: "email", message: t("forms.invalidEmail") },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder={t("profile.enterEmail")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t("profile.telephone")} name="telephone">
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder={t("profile.enterTelephone")}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={t("profile.cell")} name="cell">
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder={t("profile.enterCell")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            ) : (
              <Descriptions
                bordered
                column={descriptionColumns}
                size={isMobile ? "small" : "default"}
                labelStyle={{ width: labelWidth }}
                styles={{
                  label: { whiteSpace: "normal", wordBreak: "break-word" },
                }}
              >
                <Descriptions.Item label={t("profile.loginName")}>
                  {profile?.loginName || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.fullName")}>
                  {profile?.firstName || profile?.lastName
                    ? `${profile?.firstName || ""} ${
                        profile?.lastName || ""
                      }`.trim()
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.email")}>
                  {profile?.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.telephone")}>
                  {profile?.telephone || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.cell")}>
                  {profile?.cell || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.homePhone")}>
                  {profile?.homePhone || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.fax")}>
                  {profile?.fax || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.pager")}>
                  {profile?.pager || "-"}
                </Descriptions.Item>
              </Descriptions>
            )}

            {!editing && (
              <>
                <Divider orientation="left">
                  {t("profile.companyInformation")}
                </Divider>
                <Descriptions
                  bordered
                  column={descriptionColumns}
                  size={isMobile ? "small" : "default"}
                  labelStyle={{ width: labelWidth }}
                  styles={{
                    label: { whiteSpace: "normal", wordBreak: "break-word" },
                  }}
                >
                  <Descriptions.Item label={t("profile.companyName")}>
                    {profile?.companyName || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("profile.employeeNumber")}>
                    {profile?.employeeNumber || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("profile.mailStop")}>
                    {profile?.mailStop || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("profile.manager")}>
                    {profile?.managerName || "-"}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </Card>
        </Col>

        {/* Account Settings - Only show when not editing */}
        {!editing && (
          <Col span={24}>
            <Card
              title={t("profile.accountSettings")}
              size={isMobile ? "small" : "default"}
            >
              <Descriptions
                column={descriptionColumns}
                bordered
                size={isMobile ? "small" : "default"}
                labelStyle={{ width: labelWidth }}
                styles={{
                  label: { whiteSpace: "normal", wordBreak: "break-word" },
                }}
              >
                <Descriptions.Item label={t("profile.logonEnabled")}>
                  {profile?.logonEnabled ? t("common.yes") : t("common.no")}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.workingDirectory")}>
                  {profile?.workingDirectory || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.startingPage")}>
                  {profile?.startingPage || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("profile.defaultVault")}>
                  <span style={{ wordBreak: "break-all" }}>
                    {profile?.defaultVault || "-"}
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}
      </Row>

      {/* MFA Security Card */}
      <Row gutter={gutter} style={{ marginTop: isMobile ? 12 : 0 }}>
        <Col span={24}>
          <MfaSecurityCard />
        </Col>
      </Row>

      {/* Change Credential Modal */}
      <Modal
        title={t("profile.changeCredential")}
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={isMobile ? "95%" : 520}
        centered={isMobile}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            label={t("profile.currentCredential")}
            name="currentPassword"
            rules={[
              {
                required: true,
                message: t("profile.currentCredentialRequired"),
              },
            ]}
          >
            <Input.Password placeholder={t("profile.enterCurrentCredential")} />
          </Form.Item>
          <Form.Item
            label={t("profile.newCredential")}
            name="newPassword"
            rules={[
              { required: true, message: t("profile.newCredentialRequired") },
              { min: 6, message: t("profile.credentialMinLength") },
            ]}
          >
            <Input.Password placeholder={t("profile.enterNewCredential")} />
          </Form.Item>
          <Form.Item
            label={t("profile.confirmCredential")}
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: t("profile.confirmCredentialRequired"),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("profile.credentialMismatch")),
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder={t("profile.enterConfirmCredential")} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setPasswordModalVisible(false);
                  passwordForm.resetFields();
                }}
              >
                {t("actions.cancel")}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={changingPassword}
              >
                {t("profile.changeCredential")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
